import _extends from "@babel/runtime/helpers/extends";
import { createModelProvider } from "@icg360/rex";
import moment from "moment";
import { DATE_FORMAT_PRODUCT, DATE_FORMAT_ISO8601, RULESET_OUTCOMES } from "../constants";
import { turnXmlStringIntoDomDocument, getValuesFromTransactionRequestXml } from "../shared/xmlUtils";
import store from "../store";
/**
 * This function gets fired when the form submits. If there's a client-side
 * validation error or a server-side error, we'll fire the failed action creator
 * with a custom error object which will serve as the data which shows in the UI
 * denoting what went wrong.
 *
 * @param {Object} pacData
 * @param {String} pacData.apiPxcentralUrl
 * @param {String} pacData.pacId
 * @param {String} pacData.policyActionId
 * @param {Object} pacData.policyData
 * @param {String} pacData.policyData.InsightPolicyId
 * @param {Object} rexFormValues
 *  - key/value pairs of form values from rex
 * @param {Object} rexFormChangedValues
 *  - key/value pairs of changed form values from rex
 * @param {Object} staticFormValues
 *  - key/value pairs of the static form values
 * @param {Object} transactionRequest
 * @param {Object} transactionRequest.cachedResponse
 *  - key/value pairs of specific values from the returne XML document. These
 *    values get shown on the preview screen indicating which values will change
 *    in the policy if the transaction request is saved.
 * @param {String} transactionRequest.cachedBody
 *  - XML payload of the transaction request
 * @param {Object} transactionRequest.cachedHeaders
 *  - key/value pairs for the request headers
 * @param {Boolean} transactionRequest.hasRateValidationBlock
 */

export default function postTransactionRequest(_ref) {
  var pacData = _ref.pacData,
      _ref$pacData = _ref.pacData,
      apiPxcentralUrl = _ref$pacData.apiPxcentralUrl,
      policyActionId = _ref$pacData.policyActionId,
      InsightPolicyId = _ref$pacData.policyData.InsightPolicyId,
      rexFormChangedValues = _ref.rexFormChangedValues,
      rexRulesets = _ref.rexRulesets,
      staticFormValues = _ref.staticFormValues,
      _ref$transactionReque = _ref.transactionRequest,
      cachedResponse = _ref$transactionReque.cachedResponse,
      cachedRequestBody = _ref$transactionReque.cachedRequestBody,
      cachedRequestHeaders = _ref$transactionReque.cachedRequestHeaders,
      hasRateValidationBlock = _ref$transactionReque.hasRateValidationBlock;
  // If nothing is cached, it's because we've already requested a preview.
  // Now we're ready to save.
  var requestBody = populateRequestBody({
    payloadXmlStr: getRequestBody(store, pacData, cachedRequestBody),
    pacData: pacData,
    staticFormValues: staticFormValues,
    rexFormChangedValues: rexFormChangedValues,
    rexRulesets: rexRulesets
  });
  var requestHeaders = cachedRequestHeaders ? cachedRequestHeaders : getRequestHeaders({
    hasRateValidationBlock: hasRateValidationBlock,
    pacData: pacData,
    payloadXmlStr: requestBody
  });
  return new Promise(function (resolve, reject) {
    fetch(apiPxcentralUrl + "/policies/" + InsightPolicyId, {
      method: "POST",
      credentials: "include",
      headers: _extends({}, requestHeaders, {
        "X-Commit": Boolean(cachedRequestBody)
      }),
      body: requestBody
    }).then(function (res) {
      res.text().then(function (resText) {
        if (res.ok) {
          resolve({
            response: cachedResponse ? cachedResponse : getValuesFromTransactionRequestXml(resText, policyActionId),
            requestBody: requestBody,
            requestHeaders: requestHeaders
          });
        } else {
          reject({
            res: res,
            resText: resText
          });
        }
      });
    });
  });
}
/**
 * Derive the payload schema. Formula:
 * Root tag name + `schemaVersion` attribute, joined by "."
 *
 * @param {String} xmlString
 *   Example: "<TransactionRequest schemaVersion="1.4.">"
 * @return {String}
 *   Example: "transactionrequest.1.4"
 */

export function derivePayloadSchema(xmlString) {
  var dom = turnXmlStringIntoDomDocument(xmlString);

  if (!dom.documentElement) {
    return "";
  }

  var rootTag = dom.documentElement.tagName.toLowerCase();
  var schemaVersion = dom.documentElement.getAttribute("schemaVersion");
  return rootTag && schemaVersion ? rootTag + "." + schemaVersion : "";
}
/**
 * Have Rex give us the XML payload from the store
 * This is just a wrapper for `createModelProvider`
 *
 * @param {Object} store - redux store
 * @param {Object} pacData
 * @param {String} pacData.pacId
 * @param {String} pacData.policyActionId
 */

export function getRequestBody(storeInstance, _ref2, cachedRequestBody) {
  var pacId = _ref2.pacId,
      policyActionId = _ref2.policyActionId;
  var isCommit = !!cachedRequestBody;
  var state = storeInstance.getState();
  var formVersion = state.rex.version[pacId];

  if (formVersion > 1) {
    var _state$rex$composure;

    var _ref3 = ((_state$rex$composure = state.rex.composure) == null ? void 0 : _state$rex$composure[pacId].submissions) || {
      submit: {
        dataType: "all"
      }
    },
        submit = _ref3.submit,
        preview = _ref3.preview;

    var details = isCommit ? submit : preview || submit;
    return createModelProvider(state, {
      childId: policyActionId,
      id: pacId,
      onlyChanged: details.dataType === "changed",
      type: "XML"
    })(isCommit ? "commit" : "default");
  } // Get the payload from Rex based on the current values in Rex


  var payloadXmlStrOnlyChanged = createModelProvider(state, {
    childId: policyActionId,
    id: pacId,
    onlyChanged: true,
    type: "XML"
  });
  var payloadXmlStrAll = createModelProvider(state, {
    childId: policyActionId,
    id: pacId,
    onlyChanged: false,
    type: "XML"
  });

  if (isCommit) {
    var commitPayload = payloadXmlStrAll("commit");
    if (commitPayload) return commitPayload;
  }

  return cachedRequestBody || payloadXmlStrOnlyChanged("default");
}
/**
 * Populate our payload
 *
 * The payload template is defined in the product XML and delivered to us via
 * Rex with form values Rex is responsible for. We then fill in the payload with
 * additional values we know it needs.
 *
 * This approach allows the product implementation team to fully define products
 * on their end along with the server payload. We just fill in the empty nodes
 * we know need to be filled in. In the future, if a new node gets stuck in the
 * `<TransactionRequest>` template that we don't know about, we'll have to add
 * support for filling it in with a value here.
 *
 * Note on date values: whatever format the server expects for date values,
 * that should be the value coming from the store (this area should be
 * agnostic about what the server is expecting)
 *
 * @param {String} pacData
 * @param {String} staticFormValues.comment - form value
 * @param {String} staticFormValues.effectiveDate - form value
 * @param {String} payloadXmlStr
 * @param {object} rexFormChangedValues
 * @param {Object[]} rexRulesets - rulesets as they come from rex
 * @return {String} - `payloadXmlStr` populated with specific values
 */

export function populateRequestBody(_ref4) {
  var _ref4$pacData = _ref4.pacData,
      policyActionId = _ref4$pacData.policyActionId,
      _ref4$pacData$policyD = _ref4$pacData.policyData,
      InsightPolicyId = _ref4$pacData$policyD.InsightPolicyId,
      Version = _ref4$pacData$policyD.Version,
      ReasonCodes = _ref4$pacData.rexFormMetadata.ReasonCodes,
      username = _ref4$pacData.username,
      _ref4$staticFormValue = _ref4.staticFormValues,
      comment = _ref4$staticFormValue.comment,
      effectiveDate = _ref4$staticFormValue.effectiveDate,
      payloadXmlStr = _ref4.payloadXmlStr,
      rexFormChangedValues = _ref4.rexFormChangedValues,
      rexRulesets = _ref4.rexRulesets;
  // Convert xml string to dom node so we can easily modify
  var payloadDom = turnXmlStringIntoDomDocument(payloadXmlStr);
  var reasonCode = deriveReasonCode(ReasonCodes, rexFormChangedValues); // So allow me to explain what's going on here:
  // Most forms work in the same manner (from a UI perspective):
  //   1. choose the date you want your changes to go into effect (Pac)
  //   2. make the changes available to you in the dynamic form (REX/Product XML)
  // This pattern holds true across all our actions *except* `changeeffectivedate`
  // For that action, number 1 and 2 are essentially the same. The user does not
  // choose effective date from the hard-coded field we have in Pac. Rather they
  // choose it from the fields provided via Rex. Then right here, we have to
  // intercept that value and set it as our effective date (in a different
  // format than what it comes in). So we check if it's the change effective
  // date policy action, and if it is, we look for a node like this:
  //   <DataItem name="EffectiveDate" />
  // Then we take it's value which is MM/DD/YYYY and translate it to the format
  // our `effectiveDate` form value normally has of YYYY-MM-DD. Example:
  //   Intercept: <DataItem name="EffectiveDate" value="01/31/2018" />
  //   Set `effectiveDate` form value as "2018-01-31"

  if (policyActionId === "changeeffectivedate") {
    var $el = payloadDom.querySelector("DataItem[name='EffectiveDate']");
    var dataItemEffectiveDate = $el ? $el.getAttribute("value") : "";

    if (dataItemEffectiveDate) {
      effectiveDate = moment(dataItemEffectiveDate, DATE_FORMAT_PRODUCT).format(DATE_FORMAT_ISO8601);
    }
  } // Loop over each of these nodes and modify them in the payload DOM tree


  [{
    selector: "Comment",
    value: comment
  }, {
    selector: "EffectiveDate",
    value: effectiveDate
  }, {
    selector: "Identifier[name='InsightPolicyId']",
    attribute: "value",
    value: InsightPolicyId
  }, {
    selector: "Initiator[type='user']",
    value: username
  }, {
    selector: "SourceVersion",
    value: Version
  }, {
    selector: "StartDate",
    value: effectiveDate
  }, {
    selector: "ReasonCode",
    value: reasonCode
  }].forEach(function (payloadElement) {
    var $el = payloadDom.querySelector(payloadElement.selector); // Ensure the element exists

    if ($el) {
      // If it has an attribute, set that attribute's value
      // Otherwise just set the textContent
      if (payloadElement.attribute) {
        $el.setAttribute(payloadElement.attribute, payloadElement.value);
      } else {
        $el.textContent = payloadElement.value;
      }
    }
  }); // If the form was submitted, that means it was considered valid. That means
  // if there were ruleset blockers, the user chose to manually override them.
  // This means we can check here if there are any ruleset blocker ids present.
  // If there are, that means ruleset blockers were in place when the form was
  // submitted as an exception request, so we're going to pass those along
  // to the server so we can track how often overrides are taking place.

  var rulesetBlockerIds = rexRulesets.filter(function (r) {
    return r.outcome === RULESET_OUTCOMES.BLOCK;
  }).map(function (r) {
    return r.rulesetId;
  });

  if (rulesetBlockerIds.length) {
    var $intervalRequest = payloadDom.querySelector("IntervalRequest");

    if ($intervalRequest) {
      // prettier-ignore
      var $dom = turnXmlStringIntoDomDocument("<DataItem name=\"UWRulesetsBlockOverride\" value=\"" + rulesetBlockerIds.join(",") + "\" />", "application/xml");
      $intervalRequest.appendChild($dom.firstChild);
    } else {
      // eslint-disable-next-line no-console
      console.warn("No interval request was found in the payload. Tracking has been disabled.");
    }
  } // Serialize the XML to a string and return use as the populated payload


  return new XMLSerializer().serializeToString(payloadDom);
}
/**
 * Determine the `reasonCode` for the current form.
 *
 * Each form has a list of reason codes (defined in the product XML and
 * delivered to us from Rex via the metadata selector). Note: the reason codes
 * are ordered by most relevant to least.
 * We will walk through this list of reason codes and as soon as we find the
 * first reason code in the list that matches one of the proposed changes from
 * the form, we'll set the `reasonCode` to that reason code's value.
 *
 * @param {Object[]} reasonCodes
 *   An array of objects representing reason codes (selected from rex metadata).
 *     [
 *       { name: "FireAlarm", value: "122" },
 *       { name: "BurglarAlarm", value: "123" }
 *     ]
 * @param {Array} rexFormChangedValues
 *   A mapping of proposed changes (form values) from rex
 *     [
 *       "BurglarAlarm",
 *       "FireAlarm"
 *     ]
 * @return {String}
 *   The derived reason code as a string. Based on the examples above, the
 *   reason code would be "122" (both `BurglarAlarm` and `FireAlarm` were
 *   changed in the form, but `FireAlarm` has a higher precedence based on its
 *   order in the array of reason codes coming form the product).
 */

export function deriveReasonCode(reasonCodes, rexFormChangedValues) {
  if (reasonCodes === void 0) {
    reasonCodes = [];
  }

  if (rexFormChangedValues === void 0) {
    rexFormChangedValues = [];
  }

  // Walk through the list of reason codes in order. First one we find that
  // has a changed form value, we set the reason code and exit.
  for (var i = 0; i < reasonCodes.length; i++) {
    if (rexFormChangedValues.includes(reasonCodes[i].name)) {
      return reasonCodes[i].value;
    }
  }

  return "";
}
/**
 * Get the headers for the transaction request. Some are hardcoded here, others
 * are set dynamically based on dynamic or stateful values.
 * @param {Object}  args
 * @param {Boolean} args.hasRateValidationBlock
 * @param {Object}  args.pacData
 * @param {Object}  args.pacData.rexFormMetadata
 * @param {Object}  args.pacData.rexFormMetadata.RequestHeaders
 *   - key/value pairing of request headers specific to the policy's product
 *     (these are defined by the product team and retrieve when <Pac /> starts)
 * @param {String}  args.payloadXmlStr
 */

export function getRequestHeaders(_ref5) {
  var hasRateValidationBlock = _ref5.hasRateValidationBlock,
      RequestHeaders = _ref5.pacData.rexFormMetadata.RequestHeaders,
      payloadXmlStr = _ref5.payloadXmlStr;
  return _extends({
    // Setting this to `false` by default, we can override elsewhere.
    // true: save the request
    // false: get a preview of the change
    "X-Commit": false,
    // prettier-ignore
    "Content-Type": "application/xml; schema=" + derivePayloadSchema(payloadXmlStr),
    "X-EndorseKey": "true",
    // caching mechanism, leave as true for both
    Accept: "application/vnd.ics360.insurancepolicy.2.8+xml",
    // If the form has a rate validation error and is being submitted again,
    // that means the user was able to do so. So we use that piece of state
    // to determine whether to set this header and override the rate validation
    // i.e. if it has a rate validation error and the form gets submitted again,
    // that means we're overriding it.
    "Override-Validation-Block": hasRateValidationBlock
  }, Array.isArray(RequestHeaders) ? RequestHeaders.reduce(function (acc, _ref6) {
    var _extends2;

    var name = _ref6.name,
        value = _ref6.value;
    return _extends({}, acc, (_extends2 = {}, _extends2[name] = value, _extends2));
  }, {}) : {});
}